<template>
  <Modal
    @closed="$emit('close')"
    :open="true"
    :closable="true"
    id="templates-modal"
    title="Selecteer een template"
    class="vl-email-templates max-w-full lg:max-w-[1200px]"
  >
    <VlAlert
      v-if="error"
      :mod-error="true"
      content="Error: Het template kon niet worden geladen."
    />

    <!-- Left / Right -->
    <div class="flex flex-col lg:flex-row lg:gap-6">
      <!-- Left -->
      <div class="w-full lg:w-[380px] lg:flex-shrink-0">
        <!-- Theme selection -->
        <div v-if="questionThemes.length > 1" class="mb-4">
          <h2 class="vl-title vl-title--h2">Thema</h2>
          <select
            class="vl-select w-full"
            v-model="selectedThemeId"
            @change="changeTheme"
          >
            <option disabled>Thema</option>
            <option
              v-for="t in questionThemes"
              :key="t.id"
              :value="t.id"
            >
              {{ t.name }}
            </option>
          </select>
        </div>

        <!-- Results wrapper -->
        <div v-if="templates.length">
          <!-- Language selection -->
          <div class="flex justify-between items-center mb-4">
            <div class="vl-tabs__wrapper">
              <ul class="vl-tabs m-0" role="tablist">
                <li
                  class="vl-tab"
                  v-for="{ key, label } in filteredLanguages"
                  :class="{ 'vl-tab--active': selectedLanguage === key }"
                  :key="key"
                >
                  <a
                    @click.prevent="selectedLanguage = key"
                    class="vl-tab__link"
                    href
                  >
                    {{ label }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Search -->
          <div class="relative mb-4">
            <VlInputField v-model="searchQuery" class="w-full pl-10" />
            <VlIcon
              icon="search"
              class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            />
          </div>

          <!-- Results -->
          <ul class="max-h-[60vh] lg:max-h-[80vh] overflow-y-auto">
            <li
              v-for="{ name, id } in filteredTemplates"
              :key="id"
              class="border-b"
            >
              <div class="flex items-center justify-between cursor-pointer">
                <VlLink
                  href
                  @click.prevent="previewTemplateId = id"
                  class="p-4 block flex-grow"
                >
                  {{ name }}
                </VlLink>

                <div class="ml-4">
                  <VlButton
                    @click.prevent="selectTemplate(id)"
                    :mod-narrow="true"
                    :mod-disabled="isSelectingTemplateId != null"
                    :mod-loading="id === isSelectingTemplateId"
                  >
                    Selecteer
                  </VlButton>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- No results -->
        <div v-if="!isLoading && !templates.length">Geen resultaten</div>
      </div>

      <!-- Right (Preview) -->
      <div class="relative w-full lg:flex-1 mt-6 lg:mt-0">
        <div class="absolute inset-0 flex items-center justify-center" v-if="isLoading">
          <VlLoader />
        </div>

        <div v-else class="email-preview-container vl-typography">
          <div class="email-preview" v-html="previewHtml"></div>
        </div>

      </div>
    </div>
  </Modal>
</template>

<script>
import { allPass, get, propEq, toLower } from 'lodash/fp';
import { mapState, mapGetters } from 'vuex';
import { DUTCH } from '@/constants/languages';
import { languages } from '@/config';
import { crmAxios } from '@/utils/Axios';

const fetchTemplateHtml = id =>
  crmAxios({
    url: `/EmailTemplate/${id}`,
  });

export default {
  props: ['emailId'],

  data() {
    return {
      error: null,
      isLoading: false,
      isSelectingTemplateId: null,
      previewHtml: null,
      previewTemplateId: null,
      searchQuery: '',
      selectedLanguage: DUTCH,
      templates: [],
      selectedThemeId: null,
    };
  },

  created() {
    this.selectedThemeId = this.questionThemes.length
      ? this.questionThemes[0].id
      : null;

    this.loadTemplates();
  },

  computed: {
    ...mapState('contact', ['contact']),
    ...mapGetters('contact', ['themeId']),
    ...mapGetters('metadata', ['availableThemesByLineId']),

    themes() {
      return this.availableThemesByLineId(this.contact.lineId);
    },

    questionThemes() {
      const questionThemeIds = this.contact.questions.map(
        question => question.themeId,
      );

      return this.themes.filter(theme =>
        questionThemeIds.includes(theme.id),
      );
    },

    filteredTemplates() {
      const isInSelectedLanguage = propEq('language', this.selectedLanguage);
      const containsSearchQuery = ({ name }) =>
        toLower(name).includes(toLower(this.searchQuery));

      return this.templates.filter(
        allPass([containsSearchQuery, isInSelectedLanguage]),
      );
    },

    filteredLanguages() {
      return languages.filter(({ key }) =>
        this.templates.find(({ language }) => language === key),
      );
    },
  },

  methods: {
    async selectTemplate(id) {
      this.isSelectingTemplateId = id;

      try {
        const templateString =
          id === this.previewTemplateId
            ? this.previewHtml
            : await fetchTemplateHtml(id).then(get('data.body'));

        this.$store.dispatch(`emails/prepend`, {
          id: this.emailId,
          templateString,
        });

        console.log('Template selected:', id);
        //console.log('Template content:', templateString);

        this.$emit('close');
      } catch (error) {
        console.error(error);
        this.error = error;
        this.isSelectingTemplateId = null;
      }
    },

    loadTemplates() {
      this.isLoading = true;

      const themeId = this.selectedThemeId || this.themeId;

      crmAxios({
        url: `/EmailTemplate/theme/${themeId}`,
      })
        .then(({ data }) => {
          this.templates = data;
          this.previewTemplateId = data?.[0]?.id || null;
          this.isLoading = false;
        })
        .catch(error => {
          this.error = error;
          this.isLoading = false;
        });
    },

    changeTheme() {
      this.previewTemplateId = null;
      this.loadTemplates();
    },
  },

  watch: {
    previewTemplateId(id) {
      if (!id) {
        this.previewHtml = '';
        return;
      }

      fetchTemplateHtml(id).then(({ data }) => {
        this.previewHtml = data.body 
        .replace(/<li[^>]*>\s*<p[^>]*>(.*?)<\/p>\s*<\/li>/g, '<li>$1</li>');
      });
    },
  },
};
</script>

<style scoped>
/* Container for the email preview */
.email-preview-container {
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 1rem;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  box-sizing: border-box;
}

/* Main preview wrapper */
.email-preview {
  all: unset;
  display: block;
  width: 100%;
  font-family: inherit;  /* inherit VL-UI typography */
  font-size: inherit;
  line-height: inherit;
}

/* Force VL-UI typography, ignore all inline CKEditor styles */
.email-preview * {
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
  all: revert; /* keep block/inline behavior */
  box-sizing: border-box;
}

/* Override inline style attributes */
.email-preview *[style] {
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
}

/* Paragraphs and divs */
.email-preview p,
.email-preview div {
  margin: 0 0 1em 0;
}

/* Headings inherit VL-UI typography */
.email-preview h1,
.email-preview h2,
.email-preview h3,
.email-preview h4,
.email-preview h5,
.email-preview h6 {
  margin: 1em 0 0.5em 0;
  font-weight: inherit;
}

/* Links */
.email-preview a {
  color: #1a73e8;
  text-decoration: underline;
}

/* Images */
.email-preview img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0.5em 0;
}

/* Lists: UL and OL */
.email-preview ul,
.email-preview ol {
  list-style: disc inside !important;
  margin: 0 0 1em 1.5em !important;
  padding: 0 !important;
}

/* List items */
.email-preview li {
  margin: 0 0 0.5em 0 !important;
  padding: 0 !important;
  line-height: 1.5 !important;
  list-style-position: inside !important;
}

/* Remove extra <p> inside <li> */
.email-preview li p {
  margin: 0 !important;
  display: inline; /* keep bullet alignment */
}

/* Ensure spans inside li inherit fonts and line-height */
.email-preview li span {
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
}

/* Optional: override CKEditor wrapper classes */
/* Force VL-UI typography on CKEditor content */
.email-preview .ck-content,
.email-preview .ck-content * {
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
  all: revert !important; /* revert display/block behavior */
  box-sizing: border-box;
  margin: 0 !important;
  padding: 0 !important;
}

/* Headings inside CKEditor */
.email-preview .ck-content h1,
.email-preview .ck-content h2,
.email-preview .ck-content h3,
.email-preview .ck-content h4,
.email-preview .ck-content h5,
.email-preview .ck-content h6 {
  margin: 1em 0 0.5em 0 !important;
  font-weight: inherit !important;
}

/* Lists inside CKEditor */
.email-preview .ck-content ul,
.email-preview .ck-content ol {
  list-style: disc inside !important;
  margin: 0 0 1em 1.5em !important;
  padding: 0 !important;
}

.email-preview .ck-content li {
  margin: 0 0 0.5em 0 !important;
  padding: 0 !important;
  line-height: 1.5 !important;
}

.email-preview .ck-content li p {
  margin: 0 !important;
  display: inline !important; /* keeps bullets aligned */
}

.email-preview .ck-content li span {
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
}

/* Images inside CKEditor content */
.email-preview .ck-content img {
  max-width: 100% !important;
  height: auto !important;
  display: block !important;
  margin: 0.5em 0 !important;
}

/* Links inside CKEditor content */
.email-preview .ck-content a {
  color: #1a73e8 !important;
  text-decoration: underline !important;
}

</style>

